import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { 
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Avatar
} from "@mui/material";
import { 
  Menu as MenuIcon,
  VideoLibrary,
  Settings,
  Logout
} from "@mui/icons-material";
import { Actions as authActions } from "../../redux/auth";
import styled from "styled-components";

const LogoSection = styled(Typography)`
  padding: 0 2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NavLinksContainer = styled(Box)`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const NavLink = styled(Button)`
  white-space: nowrap;
  flex-shrink: 0;
  color: white;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

function Navbar({ user, logUserOut, style }) {
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [maxVisibleItems, setMaxVisibleItems] = useState(5);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleLogout = () => {
    logUserOut();
  };

  // Calculate how many items can fit based on screen width
  useEffect(() => {
    const calculateMaxItems = () => {
      const screenWidth = window.innerWidth;
      const buttonWidth = 105; // Each button is 95px wide
      const logoWidth = 320; // Logo width is 250px
      const hamburgerWidth = 50; // Space for hamburger button
      const padding = 40; // General padding
      
      const availableWidth = screenWidth - logoWidth - hamburgerWidth - padding;
      const maxItems = Math.floor(availableWidth / buttonWidth);
      
      // Ensure at least 1 item is always visible
      const safeMaxItems = Math.max(1, maxItems);
      
      setMaxVisibleItems(safeMaxItems);
    };

    calculateMaxItems();
    window.addEventListener('resize', calculateMaxItems);
    
    return () => {
      window.removeEventListener('resize', calculateMaxItems);
    };
  }, []);

  // Helper function to get streams from user data (handles both possible structures)
  const getUserStreams = () => {
    if (!user) return [];
    
    // Try different possible user data structures
    if (user.streams) return user.streams;
    if (user.user && user.user.streams) return user.user.streams;
    if (user.success && user.user && user.user.streams) return user.user.streams;
    
    // Check if streams are directly in the user object
    if (Array.isArray(user.streams)) return user.streams;
    
    // If no streams found, return empty array
    return [];
  };

  // Helper function to check if user is admin
  const isUserAdmin = () => {
    if (!user) return false;
    
    // Try different possible user data structures
    if (user.isAdmin !== undefined) return user.isAdmin;
    if (user.user && user.user.isAdmin !== undefined) return user.user.isAdmin;
    if (user.success && user.user && user.user.isAdmin !== undefined) return user.user.isAdmin;
    
    // Check localStorage as fallback
    const isAdminFromStorage = localStorage.getItem("isAdmin") === "true";
    if (isAdminFromStorage) return true;
    
    return false;
  };

  // Helper function to check if user is authenticated
  const isAuthenticated = () => {
    // Check if we have a session token first
    const hasSession = localStorage.getItem("session");
    if (!hasSession) return false;
    
    if (!user) return false;
    
    // Try different possible user data structures
    if (user.success) return true;
    if (user.isAuthenticated) return true;
    if (user.user) return true;
    
    // Check if we have any user data at all
    if (Object.keys(user).length > 0) return true;
    
    return false;
  };

  const streams = getUserStreams();
  const isAdmin = isUserAdmin();
  const authenticated = isAuthenticated();

  // Debug logging
  console.log('Navbar Debug:', {
    user,
    streams: streams.length,
    isAdmin,
    authenticated,
    userStructure: {
      hasUser: !!user,
      hasUserUser: !!(user && user.user),
      hasSuccess: !!(user && user.success),
      hasStreams: !!(user && user.streams),
      hasUserStreams: !!(user && user.user && user.user.streams)
    }
  });

  // Additional debugging
  console.log('Raw user object:', user);
  console.log('Streams found:', streams);
  console.log('Is admin:', isAdmin);
  console.log('Is authenticated:', authenticated);

  // Create navigation items
  const navItems = [];
  
  // Add stream links
  streams.forEach((room) => {
    navItems.push({
      id: room.id,
      label: room.name,
      icon: room.iconType || 'fullScreen',
      href: room.href || `/stream?room=${room.id}`,
      type: 'stream'
    });
  });

  // If no streams found, add some sample items for testing
  if (streams.length === 0 && authenticated) {
    navItems.push(
      { id: 'sample1', label: 'Sample Stream 1', icon: 'fullScreen', href: '/stream?room=1', type: 'stream' },
      { id: 'sample2', label: 'Sample Stream 2', icon: 'fullScreen', href: '/stream?room=2', type: 'stream' },
      { id: 'sample3', label: 'Sample Stream 3', icon: 'fullScreen', href: '/stream?room=3', type: 'stream' },
      { id: 'sample4', label: 'Sample Stream 4', icon: 'fullScreen', href: '/stream?room=4', type: 'stream' },
      { id: 'sample5', label: 'Sample Stream 5', icon: 'fullScreen', href: '/stream?room=5', type: 'stream' },
      { id: 'sample6', label: 'Sample Stream 6', icon: 'fullScreen', href: '/stream?room=6', type: 'stream' }
    );
  }

  // Add admin link
  if (isAdmin) {
    navItems.push({
      id: 'admin',
      label: 'Admin Area',
      icon: 'indexSettings',
      href: '/admin',
      type: 'admin'
    });
  }

  // Add logout link
  navItems.push({
    id: 'logout',
    label: 'Logout',
    icon: 'push',
    onClick: handleLogout,
    type: 'logout'
  });

  console.log('Navbar Items:', navItems);
  console.log('Max Visible Items:', maxVisibleItems);

  // Show items based on calculated max visible items
  const visibleItems = navItems.slice(0, maxVisibleItems);
  const hiddenItems = navItems.slice(maxVisibleItems);

  console.log('Visible Items:', visibleItems.length);
  console.log('Hidden Items:', hiddenItems.length);
  console.log('Hidden Items Data:', hiddenItems);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (item) => {
    handleMenuClose();
    if (item.onClick) {
      item.onClick();
    } else {
      console.log('Navigating to:', item.href);
      window.location.href = item.href;
    }
  };

  return (
    <AppBar position="static" style={style}>
      <Toolbar>
        <LogoSection variant="h6" component={Link} to="/" sx={{ textDecoration: 'none', color: 'inherit' }}>
          <VideoLibrary /> Video Streaming
        </LogoSection>
        
                 {authenticated && (
           <NavLinksContainer sx={{ flexGrow: 1, ml: 2 }}>
             {visibleItems.map((item) => (
               <NavLink
                 key={item.id}
                 component="button"
                                   onClick={() => {
                    if (item.onClick) {
                      item.onClick();
                    } else {
                      console.log('Visible item navigating to:', item.href);
                      window.location.href = item.href;
                    }
                  }}
                 startIcon={item.icon === 'indexSettings' ? <Settings /> : <VideoLibrary />}
               >
                 {item.label}
               </NavLink>
             ))}
           </NavLinksContainer>
         )}
        
        {authenticated && hiddenItems.length > 0 && (
          <Box sx={{ 
            padding: '2px', 
            borderRadius: '4px',
            backgroundColor: hiddenItems.length > 0 ? '#e6f3ff' : 'transparent'
          }}>
            <IconButton
              color="inherit"
              aria-label="Navigation menu"
              onClick={handleMenuClick}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {hiddenItems.map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={() => handleMenuItemClick(item)}
                  startIcon={item.icon === 'indexSettings' ? <Settings /> : item.icon === 'push' ? <Logout /> : <VideoLibrary />}
                >
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

const mapDispatchToProps = {
  logUserOut: authActions.logUserOut,
};

export default connect(mapStateToProps, mapDispatchToProps)(Navbar);